import { useState } from "react";
import { useNavigate } from "react-router";
import { Ban, FileText, Link2, Mail, MoreHorizontal, Printer, Wallet } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@bookdental/ui";
import { usePermissao } from "@/lib/use-permissao";
import { PerdaDialog } from "@/components/financeiro/a-receber/PerdaDialog";
import { useEnvioDoExtrato } from "./use-envio-do-extrato";

const item = "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-zinc-700 outline-none data-[highlighted]:bg-zinc-100 dark:text-foreground dark:data-[highlighted]:bg-muted";

type Extrato = { id: string; numero: number; status: string; saldoCents: number; enviadoEm: Date | string | null; destinatarioNome: string };

/** Os três pontinhos da linha de um extrato emitido: o que se faz sem precisar abrir. */
export function AcoesDoExtrato({ extrato: e }: { extrato: Extrato }) {
  const navigate = useNavigate();
  const pode = usePermissao("finance.manage");
  const { enviar, link } = useEnvioDoExtrato();
  const abrir = (sufixo = "") => navigate(`/financeiro/extratos/${e.id}${sufixo}`);
  const emitido = e.status === "EMITIDO";
  const [perdendo, setPerdendo] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Ações do extrato nº ${e.numero}`}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-muted dark:hover:text-foreground"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-[60] min-w-[210px] border border-zinc-200 bg-white p-1 shadow-xl dark:border-border dark:bg-card">
          <DropdownMenuItem className={item} onSelect={() => abrir()}>
            <FileText className="h-4 w-4" />
            Abrir extrato
          </DropdownMenuItem>
          {emitido && pode && (
            <DropdownMenuItem
              className={item}
              onSelect={() => window.confirm(`${e.enviadoEm ? "Reenviar" : "Enviar"} o extrato nº ${e.numero} por e-mail?`) && enviar.mutate({ extratoId: e.id })}
            >
              <Mail className="h-4 w-4" />
              {e.enviadoEm ? "Reenviar por e-mail" : "Enviar por e-mail"}
            </DropdownMenuItem>
          )}
          {emitido && pode && (
            <DropdownMenuItem className={item} onSelect={() => link.mutate({ extratoId: e.id })}>
              <Link2 className="h-4 w-4" />
              Copiar link
            </DropdownMenuItem>
          )}
          <DropdownMenuItem className={item} onSelect={() => abrir("?imprimir=1")}>
            <Printer className="h-4 w-4" />
            Baixar PDF
          </DropdownMenuItem>
          {emitido && pode && e.saldoCents > 0 && (
            <DropdownMenuItem className={item} onSelect={() => abrir("?receber=1")}>
              <Wallet className="h-4 w-4" />
              Registrar recebimento
            </DropdownMenuItem>
          )}
          {emitido && pode && e.saldoCents > 0 && (
            <DropdownMenuItem className={`${item} text-red-700 dark:text-red-400`} onSelect={() => setPerdendo(true)}>
              <Ban className="h-4 w-4" />
              Dar como perdido
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <PerdaDialog aberto={perdendo} onFechar={() => setPerdendo(false)} saldoCents={e.saldoCents} alvo={{ extratoId: e.id }} descricao={`Extrato nº ${e.numero}, ${e.destinatarioNome}`} />
    </>
  );
}
